"use client";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight, BarChart2, ShieldAlert, Cpu } from "lucide-react";
import { KineticPageShell } from "@/components/kinetic/KineticPageShell";
import { KineticWorkshopFooter } from "@/components/kinetic/KineticWorkshopFooter";

export default function ArticlePage() {
  const [comparisonMetric, setComparisonMetric] = useState<"cost" | "payload" | "eval">("cost");

  return (
    <KineticPageShell className="relative overflow-hidden">
      {/* Structural grid lines matching Cloud Shuttle privacy design */}
      <div
        className="border-kinetic-on-surface/5 pointer-events-none fixed right-0 top-0 z-0 h-screen w-1/4 border-l"
        aria-hidden
      />
      <div
        className="border-kinetic-on-surface/5 pointer-events-none fixed left-0 top-1/2 z-0 h-px w-screen border-t"
        aria-hidden
      />

      {/* Kinetic glowing blurs */}
      <div className="absolute top-[10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-kinetic-primary-container/10 blur-[120px]" />
      <div className="absolute bottom-[20%] left-[-15%] w-[600px] h-[600px] rounded-full bg-kinetic-secondary-fixed/5 blur-[150px]" />

      <main className="relative z-10 min-h-screen pb-16 pt-28 md:pb-24 md:pt-32">
        <div className="mx-auto max-w-5xl px-4 sm:px-8">
          
          <header className="relative mb-12 pl-5 md:mb-20 md:pl-0">
            <div
              className="absolute left-0 top-0 h-full w-1 bg-kinetic-primary-container md:-left-4"
              aria-hidden
            />
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <div className="mb-2 font-mono text-xs tracking-tighter text-kinetic-secondary-fixed">
                  PROTOCOL_ID: OBSERVABILITY_CORE_V1
                </div>
                <h1 className="font-headline text-4xl font-extrabold uppercase leading-none tracking-tighter text-white sm:text-5xl md:text-6xl">
                  AI Observability & Cost Evals
                </h1>
              </div>
              <div className="text-left md:text-right font-mono text-[10px] uppercase tracking-widest text-kinetic-outline">
                <div>AUTHOR: Peter Hanssens</div>
                <div className="text-kinetic-primary-container font-bold mt-1">2 June 2026</div>
              </div>
            </div>
          </header>

          {/* Intro Section */}
          <div className="kinetic-glass-panel border-kinetic-on-surface/10 relative border-l p-6 md:p-8 mb-12">
            <div className="text-kinetic-primary-fixed/30 absolute right-0 top-0 p-2 font-mono text-[8px] tracking-tighter">
              METRIC_ROUTING: ACTIVE
            </div>
            <p className="text-base font-medium leading-relaxed text-kinetic-on-surface-variant md:text-lg">
              Deploying autonomous AI agents into enterprise systems introduces a critical engineering trade-off:{" "}
              <span className="font-bold text-white">managing token runaway costs</span> and{" "}
              <span className="font-bold text-white">preventing quality decay</span>. By employing{" "}
              <span className="font-bold text-white">Bifrost</span> as a load-balancing AI Gateway and{" "}
              <span className="font-bold text-white">Langfuse</span> for tracing analytics, we gain absolute visibility over our pipelines. Here is what happens when we compare refactoring with vs. without the Drover Ontology.
            </p>
          </div>

          {/* Side-by-Side Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            
            {/* Scenario A Card */}
            <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-low p-6 flex flex-col justify-between relative group">
              <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-rose-400">
                SCENARIO_A: WITHOUT_DROVER
              </div>
              <div>
                <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb-3 mt-2">Raw Ingestion</h3>
                <p className="text-sm text-kinetic-on-surface-variant leading-relaxed mb-4">
                  The agent runs blindly, loading all codebase contents—including dependencies and build caches—into the prompt context, resulting in compilation failures and infinite loops.
                </p>
                <ul className="font-mono text-[11px] text-kinetic-outline space-y-2 pl-4 list-disc mb-6">
                  <li>CONTEXT SIZE: 4.5 MB</li>
                  <li>HALLUCINATION RISK: CRITICAL</li>
                  <li>COMPLEX RETRIES: 12 ITERATIONS</li>
                </ul>
              </div>
              <div className="border border-rose-500/20 bg-rose-950/10 p-4 rounded-xl">
                <div className="font-mono text-[9px] text-rose-400 uppercase tracking-widest mb-2">Langfuse telemetry:</div>
                <div className="flex justify-between text-xs font-mono">
                  <span>EST_COST:</span>
                  <span className="text-rose-400 font-bold">$210.72</span>
                </div>
                <div className="flex justify-between text-xs font-mono mt-1">
                  <span>BIFROST_GATE:</span>
                  <span className="text-rose-400 font-semibold">BUDGET EXCEEDED</span>
                </div>
              </div>
            </div>

            {/* Scenario B Card */}
            <div className="border-kinetic-primary-fixed/20 border-t-2 bg-kinetic-surface-container-high p-6 flex flex-col justify-between relative group">
              <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-emerald-400">
                SCENARIO_B: WITH_DROVER
              </div>
              <div>
                <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb-3 mt-2">Governed Ontology</h3>
                <p className="text-sm text-kinetic-on-surface-variant leading-relaxed mb-4">
                  The agent utilizes local sandboxed AST symbol scans and{" "}
                  <span className="font-bold text-white">Git Delta Ingestion Mode</span>, reading only changed files compared to the last committed state.
                </p>
                <ul className="font-mono text-[11px] text-kinetic-outline space-y-2 pl-4 list-disc mb-6">
                  <li>CONTEXT SIZE: 61 KB (99% REDUCTION)</li>
                  <li>SANDBOX CONTAINMENT: YAEGI VM</li>
                  <li>LOCAL VERIFICATION: DroverFsck</li>
                </ul>
              </div>
              <div className="border border-emerald-500/20 bg-emerald-950/10 p-4 rounded-xl">
                <div className="font-mono text-[9px] text-emerald-400 uppercase tracking-widest mb-2">Langfuse telemetry:</div>
                <div className="flex justify-between text-xs font-mono">
                  <span>EST_COST:</span>
                  <span className="text-emerald-400 font-bold">$0.46</span>
                </div>
                <div className="flex justify-between text-xs font-mono mt-1">
                  <span>BIFROST_GATE:</span>
                  <span className="text-emerald-400 font-semibold">SUCCESS (200 OK)</span>
                </div>
              </div>
            </div>

          </div>

          {/* Interactive Metric Switcher */}
          <div className="mb-12">
            <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white mb-4">
              Observability Metrics trace
            </h2>
            <p className="text-kinetic-on-surface-variant leading-relaxed mb-8">
              Analyze how the Bifrost budget gate and Langfuse analytical pipeline capture and evaluate execution telemetry:
            </p>

            <div className="flex flex-wrap gap-2 font-mono text-[9px] uppercase tracking-wider mb-8">
              {[
                { id: "cost", label: "01_RUNNING_COST", icon: Cpu },
                { id: "payload", label: "02_PAYLOAD_FOOTPRINT", icon: ShieldAlert },
                { id: "eval", label: "03_EVAL_SCORES", icon: BarChart2 },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => setComparisonMetric(item.id as any)}
                  className={`border px-3 py-2 transition-colors flex items-center space-x-2 ${
                    comparisonMetric === item.id
                      ? "border-kinetic-primary-container text-kinetic-primary-container bg-kinetic-surface-container-lowest"
                      : "border-kinetic-outline-variant/40 text-kinetic-on-surface-variant hover:border-kinetic-primary-container"
                  }`}
                >
                  <item.icon className="h-3 w-3 shrink-0" />
                  <span>{item.label.replace(/_/g, ' ')}</span>
                </button>
              ))}
            </div>

            {/* Dynamic Metric card */}
            <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 relative">
              <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-kinetic-primary-container">
                TRACE_INSIGHT: {comparisonMetric.toUpperCase()}_ANALYSIS
              </div>
              {comparisonMetric === "cost" && (
                <div className="space-y-4">
                  <h4 className="font-headline text-lg font-bold text-white uppercase tracking-wider">💰 450x API Token Cost Savings</h4>
                  <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                    Scenario A is blind to code boundaries, repeatedly dispatching massive 4.5 MB frames to external APIs, resulting in{" "}
                    <span className="font-bold text-white">$210.72</span> in token fees before being blocked. Under Drover, the RLM runs in Git Delta Mode, utilizing bare Go queries inside a sandboxed interpreter to refactor components for only{" "}
                    <span className="font-bold text-white">$0.46</span>—saving{" "}
                    <span className="font-bold text-white">99.7% of token fees</span>.
                  </p>
                </div>
              )}
              {comparisonMetric === "payload" && (
                <div className="space-y-4">
                  <h4 className="font-headline text-lg font-bold text-white uppercase tracking-wider">📦 99% Context Size Reduction</h4>
                  <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                    Scenario A walks files raw and ingests builds and caches. Drover&apos;s Git-driven excluder ignores cache folders and compiles only changed git lines compared to the last commit, shrinking prompt payloads from{" "}
                    <span className="font-bold text-white">4.5 MB down to 61 KB</span>.
                  </p>
                </div>
              )}
              {comparisonMetric === "eval" && (
                <div className="space-y-4">
                  <h4 className="font-headline text-lg font-bold text-white uppercase tracking-wider">📊 Langfuse Correctness Evals</h4>
                  <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                    Langfuse captures compilation correctness. Without Drover, the blind agent writes syntax and dependency violations, failing validation with an{" "}
                    <span className="font-bold text-white">Eval Score of 0.0</span>. Under Drover, the local validator runs `DroverValidate()` before submissions, producing a{" "}
                    <span className="font-bold text-white">100% compliant refactor (Eval Score 1.0)</span>.
                  </p>
                </div>
              )}
            </div>

          </div>

          {/* PR Experiment Section */}
          <div className="mb-16 mt-16 border-t border-kinetic-outline-variant/10 pt-16">
            <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white mb-4">
              🧪 The Proof: A Real-World PR Experiment
            </h2>
            <p className="text-kinetic-on-surface-variant leading-relaxed mb-8">
              To prove the effectiveness of <span className="font-bold text-white">Drover Ontology</span> when traversing highly complicated systems, we designed a specific refactoring PR challenge targeting the public <a href="https://github.com/drover-org/drover-ontology" target="_blank" rel="noopener noreferrer" className="text-kinetic-primary-container font-semibold hover:underline">drover-ontology</a> Go codebase:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Challenge Scope Card */}
              <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 rounded-xl flex flex-col justify-between">
                <div>
                  <div className="font-mono text-[9px] text-kinetic-primary-container uppercase tracking-widest mb-3">EXPERIMENT_SCOPE</div>
                  <h4 className="font-headline text-lg font-bold text-white uppercase mb-3">Enforce curatedBy Schema Property</h4>
                  <p className="text-xs text-kinetic-on-surface-variant leading-relaxed mb-4">
                    The task requires an AI agent to extend the validation engine to enforce a new strict schema metadata parameter across multiple layers:
                  </p>
                  <ul className="font-mono text-[10px] text-kinetic-outline space-y-2 list-disc pl-4">
                    <li>VALIDATION ENGINE: internal/ontology/validate.go</li>
                    <li>INTERPRETER HARNESS: tools/rlm-ontology/main_rlm.go</li>
                    <li>VISUALIZER COMMAND: commands/visualize.go</li>
                  </ul>
                </div>
                <div className="mt-6 border-t border-kinetic-outline-variant/10 pt-4 font-mono text-[9px] text-kinetic-outline-variant">
                  STATUS: COMPLEX POLYGLOT MIGRATION
                </div>
              </div>

              {/* Outcome Comparison Card */}
              <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 rounded-xl flex flex-col justify-between">
                <div>
                  <div className="font-mono text-[9px] text-kinetic-secondary-fixed uppercase tracking-widest mb-3">THE_OUTCOME</div>
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs font-bold text-rose-400 font-mono">SCENARIO A (WITHOUT DROVER)</div>
                      <p className="text-xs text-kinetic-on-surface-variant mt-1">
                        The agent edits the validation logic in the Go core but completely misses the visual sidebar panels and pre-seeded templates. The visualizer and CLI crash on startup.
                      </p>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-emerald-400 font-mono">SCENARIO B (WITH DROVER)</div>
                      <p className="text-xs text-kinetic-on-surface-variant mt-1">
                        The agent queries the Drover Knowledge Graph first, instantly mapping the <code className="font-mono text-white">Term:validation-policy</code> relations. It refactors all 3 directories perfectly in a single turn.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 border-t border-kinetic-outline-variant/10 pt-4 font-mono text-[9px] text-emerald-400 font-bold uppercase tracking-wider flex items-center space-x-1">
                  <span>RESULT: SINGLE-TURN SUCCESS ($0.46)</span>
                </div>
              </div>

            </div>
          </div>

          {/* Docker Compose Section */}
          <div className="mb-16 mt-16">
            <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white mb-4">
              🐳 Local Observability Sandbox
            </h2>
            <p className="text-kinetic-on-surface-variant leading-relaxed mb-6">
              Run Langfuse v3 and Bifrost via Docker, then build Drover from source. Langfuse 2 reached end-of-life in early 2025; there is no published{" "}
              <code className="font-mono text-kinetic-outline">ghcr.io/drover-org/drover-visualizer</code> image—build the harness from the{" "}
              <a href="https://github.com/drover-org/drover-ontology" target="_blank" rel="noopener noreferrer" className="text-kinetic-primary-container font-semibold hover:underline">drover-ontology</a> repo instead.
            </p>

            <h3 className="font-mono text-xs uppercase text-kinetic-secondary-fixed tracking-widest mb-3">01 — Langfuse v3 (official compose)</h3>
            <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 rounded-xl font-mono text-xs overflow-x-auto mb-8 text-kinetic-outline-variant">
              <pre className="text-kinetic-outline leading-relaxed">{`# From https://github.com/langfuse/langfuse/blob/main/docker-compose.yml
curl -LO https://raw.githubusercontent.com/langfuse/langfuse/main/docker-compose.yml

# Replace every # CHANGEME secret before production use
docker compose up -d

# UI: http://localhost:3000`}</pre>
            </div>

            <h3 className="font-mono text-xs uppercase text-kinetic-secondary-fixed tracking-widest mb-3">02 — Bifrost gateway (config.json budgets)</h3>
            <p className="text-sm text-kinetic-on-surface-variant leading-relaxed mb-4">
              Bifrost budgets are defined in <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">config.json</code> under{" "}
              <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">governance.budgets</code>—not via a{" "}
              <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">BIFROST_BUDGETS_FILE</code> env var. See the{" "}
              <a href="https://docs.getbifrost.ai/deployment-guides/config-json/governance" target="_blank" rel="noopener noreferrer" className="text-kinetic-primary-container font-semibold hover:underline">Bifrost governance docs</a>.
            </p>
            <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 rounded-xl font-mono text-xs overflow-x-auto mb-4 text-kinetic-outline-variant">
              <pre className="text-kinetic-outline leading-relaxed">{`# bifrost-data/config.json (excerpt)
{
  "$schema": "https://www.getbifrost.ai/schema",
  "providers": {
    "openai": {
      "keys": [{
        "name": "openai-primary",
        "value": "env.OPENAI_API_KEY",
        "models": ["gpt-4o"],
        "weight": 1.0
      }]
    }
  },
  "governance": {
    "virtual_keys": [{
      "id": "vk-refactor-loop",
      "name": "monorepo-refactoring",
      "is_active": true,
      "provider_configs": [{
        "id": 1,
        "provider": "openai",
        "weight": 1.0,
        "allowed_models": ["gpt-4o"]
      }]
    }],
    "budgets": [{
      "id": "budget-refactor-loop",
      "virtual_key_id": "vk-refactor-loop",
      "max_limit": 200.00,
      "reset_duration": "1M"
    }]
  },
  "config_store": {
    "enabled": true,
    "type": "sqlite",
    "config": { "path": "./config.db" }
  }
}`}</pre>
            </div>
            <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 rounded-xl font-mono text-xs overflow-x-auto mb-8 text-kinetic-outline-variant">
              <pre className="text-kinetic-outline leading-relaxed">{`# docker-compose.bifrost.yml
services:
  bifrost:
    image: maximhq/bifrost:latest
    container_name: bifrost-gateway
    ports:
      - "8080:8080"
    volumes:
      - ./bifrost-data:/app/data
    environment:
      OPENAI_API_KEY: \${OPENAI_API_KEY}

# Gateway: http://localhost:8080/v1`}</pre>
            </div>

            <h3 className="font-mono text-xs uppercase text-kinetic-secondary-fixed tracking-widest mb-3">03 — Drover harness (build from source)</h3>
            <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 rounded-xl font-mono text-xs overflow-x-auto text-kinetic-outline-variant">
              <pre className="text-kinetic-outline leading-relaxed">{`git clone https://github.com/drover-org/drover-ontology.git
cd drover-ontology
make build

# Governed delta loop (Scenario B)
./bin/rlm-ontology -delta .

# Interactive visualizer — run locally from commands/visualize.go
# (no published container image)`}</pre>
            </div>
            
            <div className="mt-4 font-mono text-[10px] text-kinetic-outline-variant uppercase tracking-wider flex items-center space-x-2">
              <span className="h-1.5 w-1.5 rounded-full bg-kinetic-primary-container animate-pulse" />
              <span>Target Repository: github.com/drover-org/drover-ontology</span>
            </div>
          </div>

          {/* Step-by-Step Playbook Section */}
          <div className="mb-16">
            <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white mb-6">
              🚀 Experiment Observation Playbook
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Playbook Steps */}
              <div className="space-y-6">
                <h3 className="font-mono text-xs uppercase text-kinetic-secondary-fixed tracking-widest border-b border-kinetic-outline-variant/20 pb-2">
                  01_EXECUTION_STEPS
                </h3>
                <ol className="space-y-4 text-sm text-kinetic-on-surface-variant list-decimal pl-4">
                  <li>
                    <span className="text-white font-bold">Clone Target Codebase:</span>
                    <p className="text-xs text-kinetic-outline mt-1 font-mono">git clone https://github.com/drover-org/drover-ontology.git</p>
                  </li>
                  <li>
                    <span className="text-white font-bold">Launch Langfuse v3:</span>
                    <p className="text-xs text-kinetic-outline mt-1">Download the official compose file, set secrets, and run <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">docker compose up -d</code>. UI at <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">http://localhost:3000</code>.</p>
                  </li>
                  <li>
                    <span className="text-white font-bold">Start Bifrost:</span>
                    <p className="text-xs text-kinetic-outline mt-1">Mount <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">bifrost-data/config.json</code> with <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">governance.budgets</code>, then <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">docker compose -f docker-compose.bifrost.yml up -d</code>.</p>
                  </li>
                  <li>
                    <span className="text-white font-bold">Simulate Scenario A:</span>
                    <p className="text-xs text-kinetic-outline mt-1">Route a standard dynamic agent walk through Bifrost at <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">http://localhost:8080/v1</code>, passing your virtual key via the <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">x-bf-vk</code> header.</p>
                  </li>
                  <li>
                    <span className="text-white font-bold">Execute Scenario B:</span>
                    <p className="text-xs text-kinetic-outline mt-1">Run the compiled Go RLM loop in Git-Delta mode: <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">./bin/rlm-ontology -delta .</code></p>
                  </li>
                </ol>
              </div>

              {/* What to look for */}
              <div className="space-y-6">
                <h3 className="font-mono text-xs uppercase text-kinetic-primary-container tracking-widest border-b border-kinetic-outline-variant/20 pb-2">
                  02_WHAT_TO_OBSERVE
                </h3>
                <ul className="space-y-4 text-sm text-kinetic-on-surface-variant">
                  <li className="flex items-start space-x-3">
                    <span className="h-2 w-2 rounded-full bg-rose-400 mt-2 shrink-0" />
                    <div>
                      <span className="text-white font-bold">Bifrost Budget Gating (HTTP 429)</span>
                      <p className="text-xs text-kinetic-outline mt-1">Watch Scenario A's infinite loop hit the hard $200 limit and get safely blocked, recorded in logs via <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">docker logs bifrost-gateway</code>.</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="h-2 w-2 rounded-full bg-kinetic-primary-container mt-2 shrink-0" />
                    <div>
                      <span className="text-white font-bold">Langfuse Trace Payload Differences</span>
                      <p className="text-xs text-kinetic-outline mt-1">Open the Langfuse dashboard at <code className="font-mono bg-kinetic-surface-container-low px-1 rounded">http://localhost:3000</code>. Contrast Scenario A&apos;s massive 3.5M+ input tokens with Scenario B&apos;s compact 45K token tree.</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 mt-2 shrink-0" />
                    <div>
                      <span className="text-white font-bold">Closed-Loop Evaluation Correctness</span>
                      <p className="text-xs text-kinetic-outline mt-1">Check the "Evals" tab inside Langfuse. Notice Scenario A failing compilation with an Eval score of <code className="text-rose-400 font-bold">0.0</code> vs Scenario B scoring a clean <code className="text-emerald-400 font-bold">1.0</code>.</p>
                    </div>
                  </li>
                </ul>
              </div>

            </div>
          </div>

          {/* Action call to action */}
          <div className="border-kinetic-error/20 bg-kinetic-error-container/5 relative mt-16 border-2 border-dashed p-8 text-center max-w-3xl mx-auto">
            <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-kinetic-primary-fixed">
              SYSTEM_BOOTSTRAP_ACTION
            </div>
            <h3 className="font-headline text-xl font-bold uppercase tracking-tight text-white mb-3">
              Deploy Governed Ingestion Loops
            </h3>
            <p className="text-sm leading-relaxed text-kinetic-on-surface-variant max-w-xl mx-auto mb-6">
              Ready to eliminate codebase drift and enforce architectural policies at scale? Deploy the local visualizer and deep-link your design models directly into VS Code or Cursor natively.
            </p>
            <Link
              href="/contact-us"
              className="hover:bg-kinetic-primary-fixed/5 relative inline-block border border-kinetic-primary-fixed px-8 py-3 font-headline text-xs font-bold uppercase tracking-widest text-kinetic-primary-fixed transition-all"
            >
              <span
                className="absolute -left-1 -top-1 h-2 w-2 border-l-2 border-t-2 border-kinetic-primary-fixed"
                aria-hidden
              />
              <span
                className="absolute -bottom-1 -right-1 h-2 w-2 border-b-2 border-r-2 border-kinetic-primary-fixed"
                aria-hidden
              />
              BOOK_FREE_CONSULTATION
            </Link>
          </div>

        </div>
      </main>

      <div className="relative z-10">
        <KineticWorkshopFooter />
      </div>
    </KineticPageShell>
  );
}
